import React, { useEffect, useState, lazy, createElement } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../i18n/LanguageContext';
import { Shield, Users, CheckCircle, FileText, Search, ClipboardCheck, UserCheck, Plane, Lock } from 'lucide-react';
import { submitForm } from '../utils/submitForm';
import { LoaderIcon, AlertCircle } from 'lucide-react';
import { CVFormModal } from '../components/CVFormModal';
import { CompanyFormModal } from '../components/CompanyFormModal';
export function CompaniesPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const {
    t,
    language
  } = useLanguage();
  // Form state
  const [companyName, setCompanyName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [vesselType, setVesselType] = useState('');
  const [positions, setPositions] = useState('');
  const [crewNumber, setCrewNumber] = useState('');
  const [location, setLocation] = useState('');
  const [additional, setAdditional] = useState('');
  const [startDate, setStartDate] = useState('');
  const [duration, setDuration] = useState('');
  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  // Hero modal state
  const [cvModalOpen, setCvModalOpen] = useState(false);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  // SEO: Set document title, meta description, and FAQ structured data
  useEffect(() => {
    document.title = t('companies.seo.title');
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', t('companies.seo.description'));
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = window.location.origin + window.location.pathname;
    // Open Graph tags
    const ogTags: Record<string, string> = {
      'og:title': t('companies.seo.title'),
      'og:description': t('companies.seo.description'),
      'og:type': 'website',
      'og:url': window.location.origin + window.location.pathname,
      'og:image': 'https://cdn.magicpatterns.com/uploads/bsYH7gzRmk2Ltz4BWbC6m7/IMG_0080.jpg',
      'og:site_name': 'ELVORA Consulting'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let meta = document.querySelector(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    });
    // Twitter Card tags
    const twitterTags: Record<string, string> = {
      'twitter:card': 'summary_large_image',
      'twitter:title': t('companies.seo.title'),
      'twitter:description': t('companies.seo.description'),
      'twitter:image': 'https://cdn.magicpatterns.com/uploads/bsYH7gzRmk2Ltz4BWbC6m7/IMG_0080.jpg'
    };
    Object.entries(twitterTags).forEach(([name, content]) => {
      let meta = document.querySelector(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    });
    // Preload this page's hero image
    let heroPreload = document.getElementById('page-hero-preload') as HTMLLinkElement;
    if (!heroPreload) {
      heroPreload = document.createElement('link') as HTMLLinkElement;
      heroPreload.id = 'page-hero-preload';
      heroPreload.rel = 'preload';
      heroPreload.as = 'image';
      document.head.appendChild(heroPreload);
    }
    heroPreload.href = 'https://cdn.magicpatterns.com/uploads/bsYH7gzRmk2Ltz4BWbC6m7/IMG_0080.jpg';
    // FAQPage structured data (JSON-LD)
    const faqItems = t('companies.faq.items');
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map((item: any) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.a
        }
      }))
    };
    let scriptTag = document.getElementById('companies-faq-structured-data');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.id = 'companies-faq-structured-data';
      scriptTag.setAttribute('type', 'application/ld+json');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(faqSchema);
    return () => {
      const existingScript = document.getElementById('companies-faq-structured-data');
      if (existingScript) existingScript.remove();
      const preload = document.getElementById('page-hero-preload');
      if (preload) preload.remove();
    };
  }, [t]);
  // Data from translations
  const services = [{
    icon: Users,
    ...t('companies.services.items.recruitment')
  }, {
    icon: Search,
    ...t('companies.services.items.screening')
  }, {
    icon: ClipboardCheck,
    ...t('companies.services.items.compliance')
  }, {
    icon: Plane,
    ...t('companies.services.items.coordination')
  }];
  const steps = [{
    number: '01',
    icon: FileText,
    ...t('companies.steps.items')[0]
  }, {
    number: '02',
    icon: Search,
    ...t('companies.steps.items')[1]
  }, {
    number: '03',
    icon: UserCheck,
    ...t('companies.steps.items')[2]
  }, {
    number: '04',
    icon: Plane,
    ...t('companies.steps.items')[3]
  }];
  const compliance = [{
    icon: Lock,
    ...t('companies.compliance.items.nda')
  }, {
    icon: FileText,
    ...t('companies.compliance.items.verification')
  }, {
    icon: CheckCircle,
    ...t('companies.compliance.items.stcw')
  }];
  const faqs = t('companies.faq.items');
  const vesselTypes = t('companies.form.vesselTypes');
  const regions = t('companies.form.regions');
  const durations = t('companies.form.durations');
  const rankOptions = [{
    category: t('specialists.form.ranks.deck'),
    options: [t('specialists.form.ranks.options.master'), t('specialists.form.ranks.options.chiefMate'), t('specialists.form.ranks.options.secondMate'), t('specialists.form.ranks.options.thirdMate'), t('specialists.form.ranks.options.bosun'), t('specialists.form.ranks.options.ab'), t('specialists.form.ranks.options.os')]
  }, {
    category: t('specialists.form.ranks.engine'),
    options: [t('specialists.form.ranks.options.chiefEng'), t('specialists.form.ranks.options.secondEng'), t('specialists.form.ranks.options.thirdEng'), t('specialists.form.ranks.options.fourthEng'), t('specialists.form.ranks.options.eto'), t('specialists.form.ranks.options.asstEto'), t('specialists.form.ranks.options.motorman'), t('specialists.form.ranks.options.wiper')]
  }, {
    category: t('specialists.form.ranks.other'),
    options: [t('specialists.form.ranks.options.dpo'), t('specialists.form.ranks.options.sdpo'), t('specialists.form.ranks.options.craneOp'), t('specialists.form.ranks.options.rigger'), t('specialists.form.ranks.options.electrician'), t('specialists.form.ranks.options.mechanic'), t('specialists.form.ranks.options.other')]
  }];
  const resetForm = () => {
    setCompanyName('');
    setContactPerson('');
    setEmail('');
    setPhone('');
    setVesselType('');
    setPositions('');
    setCrewNumber('');
    setLocation('');
    setAdditional('');
    setStartDate('');
    setDuration('');
    setAgreedToTerms(false);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedToTerms || submitting) return;
    setSubmitting(true);
    setSubmitStatus('idle');
    const messageParts: string[] = [];
    if (vesselType) messageParts.push(`Vessel: ${vesselType}`);
    if (positions) messageParts.push(`Positions: ${positions}`);
    if (crewNumber) messageParts.push(`Crew: ${crewNumber}`);
    if (location) messageParts.push(`Region: ${location}`);
    if (startDate) messageParts.push(`Start: ${startDate}`);
    if (duration) messageParts.push(`Duration: ${duration}`);
    if (additional) messageParts.push(`Req: ${additional.slice(0, 300)}`);
    const fd = new FormData();
    fd.append('name', companyName);
    fd.append('email', email);
    fd.append('phone', phone);
    fd.append('message', messageParts.join(' | '));
    try {
      const result = await submitForm('companies', fd, language);
      if (result.ok) {
        setSubmitStatus('success');
        resetForm();
        setTimeout(() => setSubmitStatus('idle'), 4000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };
  return <div className="min-h-screen bg-white" data-id="element-597">
      <Header data-id="element-598" />

      {/* Hero Section */}
      <section className="w-full px-6 md:px-12 py-8" data-id="element-599">
        <div className="max-w-7xl mx-auto" data-id="element-600">
          {/* Desktop */}
          <div className="hidden md:block relative w-full rounded-2xl overflow-hidden" data-id="element-601">
            <img src="https://cdn.magicpatterns.com/uploads/bsYH7gzRmk2Ltz4BWbC6m7/IMG_0080.jpg" alt="Aerial view of industrial port with cargo ships" className="w-full h-[600px] lg:h-[700px] object-cover object-center" fetchPriority="high" decoding="async" width={1400} height={700} sizes="(min-width: 1280px) 1200px, 100vw" data-id="element-602" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" data-id="element-603" />
            <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-12 pb-20 lg:pb-24" data-id="element-604">
              <div className="max-w-2xl" data-id="element-605">
                <h1 className="text-3xl lg:text-5xl font-bold text-white mb-4 leading-tight drop-shadow-lg" data-id="element-606">
                  {t('companies.hero.title')}
                </h1>
                <p className="text-white/95 text-base lg:text-lg leading-relaxed mb-2 drop-shadow-md" data-id="element-607">
                  {t('companies.hero.desc1')}
                </p>
                <p className="text-white/95 text-base lg:text-lg leading-relaxed mb-6 drop-shadow-md" data-id="element-608">
                  {t('companies.hero.desc2')}
                </p>
                <div className="flex flex-col sm:flex-row gap-3" data-id="element-609">
                  <button onClick={() => setCompanyModalOpen(true)} className="px-6 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-md hover:opacity-90 transition-opacity shadow-lg inline-flex items-center justify-center" data-id="element-610">
                    {t('hero.discussTask')}
                  </button>
                  <button onClick={() => setCvModalOpen(true)} className="px-6 py-3 bg-white/95 text-[var(--color-dark)] text-sm font-medium rounded-md hover:bg-white transition-colors shadow-lg inline-flex items-center justify-center" data-id="element-611">
                    {t('hero.submitProfile')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile */}
          <div className="md:hidden space-y-6 pb-20" data-id="element-612">
            <h1 className="text-3xl font-bold text-[var(--color-dark)] leading-tight text-center" data-id="element-613">
              {t('companies.hero.title')}
            </h1>
            <div className="relative w-full rounded-2xl overflow-hidden" data-id="element-614">
              <img src="https://cdn.magicpatterns.com/uploads/bsYH7gzRmk2Ltz4BWbC6m7/IMG_0080.jpg" alt="Aerial view of industrial port" className="w-full h-[300px] object-cover object-center" fetchPriority="high" decoding="async" width={800} height={300} sizes="100vw" data-id="element-615" />
            </div>
            <p className="text-[var(--color-dark)]/80 text-sm leading-relaxed text-center" data-id="element-616">
              {t('companies.hero.desc1')}
            </p>
            <div className="flex flex-col gap-3" data-id="element-617">
              <button onClick={() => setCompanyModalOpen(true)} className="w-full px-5 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-md hover:opacity-90 transition-opacity inline-flex items-center justify-center" data-id="element-618">
                {t('hero.discussTask')}
              </button>
              <button onClick={() => setCvModalOpen(true)} className="w-full px-5 py-3 bg-[var(--color-tan)] text-white text-sm font-medium rounded-md hover:opacity-90 transition-opacity inline-flex items-center justify-center" data-id="element-619">
                {t('hero.submitProfile')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <div className="cv-lazy-section" data-id="element-620">
        <section className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-621">
          <div className="max-w-7xl mx-auto" data-id="element-622">
            <div className="inline-block px-4 py-1.5 border border-gray-200 rounded-full mb-8" data-id="element-623">
              <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-624">
                {t('companies.services.pill')}
              </span>
            </div>

            <div className="flex flex-col lg:flex-row gap-12" data-id="element-625">
              <div className="lg:w-2/5" data-id="element-626">
                <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-6 leading-tight whitespace-pre-line" data-id="element-627">
                  {t('companies.services.title')}
                </h2>
                <p className="text-[var(--color-text-light)] text-sm leading-relaxed mb-4" data-id="element-628">
                  {t('companies.services.desc1')}
                </p>
                <p className="text-[var(--color-text-light)] text-sm leading-relaxed" data-id="element-629">
                  {t('companies.services.desc2')}
                </p>
              </div>

              <div className="lg:w-3/5" data-id="element-630">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-631">
                  {services.map((service, index) => <div key={index} className="p-6 bg-white border border-gray-100 rounded-xl" data-id="element-632">
                      <service.icon className="w-6 h-6 text-[var(--color-tan)] mb-4" strokeWidth={1.5} data-id="element-633" />
                      <h3 className="text-base font-semibold text-[var(--color-text-dark)] mb-2" data-id="element-634">
                        {service.title}
                      </h3>
                      <p className="text-sm text-[var(--color-text-light)]" data-id="element-635">
                        {service.desc}
                      </p>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* How We Work - Same design as SpecialistsPage */}
      <div className="cv-lazy-section" data-id="element-636">
        <section className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-637">
          <div className="max-w-5xl mx-auto" data-id="element-638">
            <div className="text-center mb-10" data-id="element-639">
              <div className="inline-block px-4 py-1.5 border border-gray-200 rounded-full mb-5 bg-white" data-id="element-640">
                <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-641">
                  {t('companies.steps.pill')}
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-3" data-id="element-642">
                {t('companies.steps.title')}
              </h2>
              <p className="text-base text-gray-600 max-w-xl mx-auto" data-id="element-643">
                {t('companies.steps.desc')}
              </p>
            </div>

            {/* Desktop: Zigzag Cards with Connecting Lines */}
            <div className="hidden md:block relative" data-id="element-644">
              {/* Main vertical center line */}
              <div className="absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-px bg-gray-200" data-id="element-645" />

              <div className="space-y-5 relative" data-id="element-646">
                {steps.map((step, index) => {
                const isRight = index % 2 === 0;
                const StepIcon = step.icon;
                return <div key={index} className="relative" data-id="element-647">
                      {/* Horizontal branch line */}
                      <div className={`absolute top-1/2 -translate-y-1/2 h-px bg-gray-200 ${isRight ? 'left-1/2 w-[8%]' : 'right-1/2 w-[8%]'}`} data-id="element-648" />

                      {/* Center dot */}
                      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-[var(--color-tan)] z-10" data-id="element-649" />

                      {/* Card */}
                      <div className={`flex ${isRight ? 'justify-end' : 'justify-start'}`} data-id="element-650">
                        <div className="w-[42%]" data-id="element-651">
                          <div className="bg-white border border-gray-200 rounded-xl p-5 relative" data-id="element-652">
                            <span className="absolute top-3 right-4 text-xs font-medium text-gray-400" data-id="element-653">
                              {step.number}
                            </span>
                            <div className="flex items-center gap-4 pr-10" data-id="element-654">
                              <div className="w-10 h-10 rounded-lg bg-[var(--color-tan)]/10 flex items-center justify-center flex-shrink-0" data-id="element-655">
                                <StepIcon className="w-5 h-5 text-[var(--color-tan)]" data-id="element-656" />
                              </div>
                              <div className="flex-1" data-id="element-657">
                                <h3 className="text-base font-semibold text-[var(--color-text-dark)] mb-1" data-id="element-658">
                                  {step.title}
                                </h3>
                                <p className="text-sm text-gray-500 leading-relaxed" data-id="element-659">
                                  {step.desc}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>;
              })}
              </div>
            </div>

            {/* Mobile: Vertical cards with connecting line */}
            <div className="md:hidden relative" data-id="element-660">
              <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200" data-id="element-661" />

              <div className="space-y-4" data-id="element-662">
                {steps.map((step, index) => {
                const StepIcon = step.icon;
                return <div key={index} className="relative flex gap-4" data-id="element-663">
                      <div className="w-8 flex-shrink-0 flex justify-center relative z-10" data-id="element-664">
                        <div className="w-2 h-2 rounded-full bg-[var(--color-tan)] mt-6" data-id="element-665" />
                      </div>
                      <div className="flex-1 bg-white border border-gray-200 rounded-xl p-4 relative" data-id="element-666">
                        <span className="absolute top-3 right-3 text-xs font-medium text-gray-400" data-id="element-667">
                          {step.number}
                        </span>
                        <div className="flex items-center gap-3 pr-8" data-id="element-668">
                          <div className="w-9 h-9 rounded-lg bg-[var(--color-tan)]/10 flex items-center justify-center flex-shrink-0" data-id="element-669">
                            <StepIcon className="w-4 h-4 text-[var(--color-tan)]" data-id="element-670" />
                          </div>
                          <div className="flex-1" data-id="element-671">
                            <h3 className="text-sm font-semibold text-[var(--color-text-dark)] mb-0.5" data-id="element-672">
                              {step.title}
                            </h3>
                            <p className="text-xs text-gray-500" data-id="element-673">{step.desc}</p>
                          </div>
                        </div>
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Compliance & Confidentiality */}
      <div className="cv-lazy-section-sm" data-id="element-674">
        <section className="w-full px-6 md:px-12 py-16 bg-[var(--color-bg)]" data-id="element-675">
          <div className="max-w-4xl mx-auto text-center" data-id="element-676">
            <div className="inline-block px-4 py-1.5 border border-gray-300 rounded-full mb-6 bg-white" data-id="element-677">
              <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-678">
                {t('companies.compliance.pill')}
              </span>
            </div>
            <h2 className="text-3xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-679">
              {t('companies.compliance.title')}
            </h2>
            <p className="text-lg text-[var(--color-text-light)] mb-10" data-id="element-680">
              {t('companies.compliance.desc')}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-681">
              {compliance.map((item, index) => <div key={index} className="p-6 bg-white rounded-xl border border-gray-200" data-id="element-682">
                  <div className="w-12 h-12 rounded-full bg-[var(--color-tan)]/10 flex items-center justify-center mx-auto mb-4" data-id="element-683">
                    <item.icon className="w-6 h-6 text-[var(--color-tan)]" data-id="element-684" />
                  </div>
                  <h3 className="text-base font-semibold text-[var(--color-text-dark)] mb-2" data-id="element-685">
                    {item.title}
                  </h3>
                  <p className="text-sm text-[var(--color-text-light)]" data-id="element-686">
                    {item.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </section>
      </div>

      {/* FAQ */}
      <div className="cv-lazy-section" data-id="element-687">
        <section className="w-full px-6 md:px-12 py-16 bg-white" data-id="element-688">
          <div className="max-w-4xl mx-auto text-center" data-id="element-689">
            <div className="inline-block px-4 py-1.5 border border-gray-200 rounded-full mb-6" data-id="element-690">
              <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-691">
                {t('companies.faq.pill')}
              </span>
            </div>
            <h2 className="text-3xl font-bold text-[var(--color-text-dark)] mb-12" data-id="element-692">
              {t('companies.faq.title')}
            </h2>

            <div className="bg-white rounded-2xl p-6 md:p-8 border-2 border-gray-200 text-left" data-id="element-693">
              <div className="space-y-0" data-id="element-694">
                {faqs.map((faq: any, index: number) => <div key={index} className="border-b border-gray-100 last:border-0" data-id="element-695">
                    <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full py-5 flex items-start justify-between text-left gap-4" aria-expanded={openFaq === index} data-id="element-696">
                      <h3 className="text-base font-medium text-[var(--color-text-dark)]" data-id="element-697">
                        {faq.q}
                      </h3>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${openFaq === index ? 'bg-[var(--color-tan)]' : 'bg-gray-100'}`} data-id="element-698">
                        <svg className={`w-4 h-4 transition-colors ${openFaq === index ? 'text-white' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-699">
                          {openFaq === index ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" data-id="element-700" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" data-id="element-701" />}
                        </svg>
                      </div>
                    </button>
                    {/* Answer always in DOM for SEO — visually hidden when collapsed */}
                    <div className={`overflow-hidden transition-all duration-200 ${openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-702">
                      <p className="pb-5 text-sm text-[var(--color-text-light)]" data-id="element-703">
                        {faq.a}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Request Form */}
      <div className="cv-lazy-section" data-id="element-704">
        <section id="request-form" className="w-full px-6 md:px-12 py-16 bg-[var(--color-bg)]" data-id="element-705">
          <div className="max-w-4xl mx-auto text-center" data-id="element-706">
            <div className="inline-block px-4 py-1.5 border border-gray-300 rounded-full mb-6 bg-white" data-id="element-707">
              <span className="text-sm font-medium text-[var(--color-text-dark)]" data-id="element-708">
                {t('companies.form.pill')}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text-dark)] mb-4" data-id="element-709">
              {t('companies.form.title')}
            </h2>
            <p className="text-lg text-[var(--color-text-light)] mb-2" data-id="element-710">
              {t('companies.form.desc1')}
            </p>
            <p className="text-base text-[var(--color-text-light)] mb-8" data-id="element-711">
              {t('companies.form.desc2')}
            </p>

            <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 md:p-8 text-left" data-id="element-712">
              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-713">
                {/* Company Information */}
                <div data-id="element-714">
                  <h3 className="text-lg font-semibold text-[var(--color-text-dark)] mb-4" data-id="element-715">
                    {t('companies.form.sections.company')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-716">
                    <div data-id="element-717">
                      <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-718">
                        {t('companies.form.labels.companyName')}{' '}
                        <span className="text-red-500" data-id="element-719">*</span>
                      </label>
                      <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} required className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-720" />
                    </div>
                    <div data-id="element-721">
                      <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-722">
                        {t('companies.form.labels.contactPerson')}{' '}
                        <span className="text-red-500" data-id="element-723">*</span>
                      </label>
                      <input type="text" value={contactPerson} onChange={e => setContactPerson(e.target.value)} required className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-724" />
                    </div>
                    <div data-id="element-725">
                      <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-726">
                        {t('companies.form.labels.email')}{' '}
                        <span className="text-red-500" data-id="element-727">*</span>
                      </label>
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-728" />
                    </div>
                    <div data-id="element-729">
                      <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-730">
                        {t('companies.form.labels.phone')}{' '}
                        <span className="text-red-500" data-id="element-731">*</span>
                      </label>
                      <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} required placeholder="+1 234 567 8900" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-732" />
                    </div>
                  </div>
                </div>

                {/* Request Details */}
                <div data-id="element-733">
                  <h3 className="text-lg font-semibold text-[var(--color-text-dark)] mb-4" data-id="element-734">
                    {t('companies.form.sections.details')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-735">
                    <div data-id="element-736">
                      <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-737">
                        {t('companies.form.labels.vesselType')}{' '}
                        <span className="text-red-500" data-id="element-738">*</span>
                      </label>
                      <select value={vesselType} onChange={e => setVesselType(e.target.value)} required className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors appearance-none bg-white" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath fill='%23666' d='M4.427 6.427l3.396 3.396a.25.25 0 00.354 0l3.396-3.396A.25.25 0 0011.396 6H4.604a.25.25 0 00-.177.427z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 12px center'
                    }} data-id="element-739">
                        <option value="" data-id="element-740">
                          {t('companies.form.labels.selectVessel')}
                        </option>
                        {vesselTypes.map((type: string) => <option key={type} value={type} data-id="element-741">
                            {type}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-742">
                      <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-743">
                        {t('companies.form.labels.positions')}{' '}
                        <span className="text-red-500" data-id="element-744">*</span>
                      </label>
                      <select value={positions} onChange={e => setPositions(e.target.value)} required className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors appearance-none bg-white" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath fill='%23666' d='M4.427 6.427l3.396 3.396a.25.25 0 00.354 0l3.396-3.396A.25.25 0 0011.396 6H4.604a.25.25 0 00-.177.427z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 12px center'
                    }} data-id="element-745">
                        <option value="" data-id="element-746">
                          {t('companies.form.labels.selectPositions')}
                        </option>
                        {rankOptions.map(group => <optgroup key={group.category} label={group.category} data-id="element-747">
                            {group.options.map(option => <option key={option} value={option} data-id="element-748">
                                {option}
                              </option>)}
                          </optgroup>)}
                      </select>
                    </div>
                    <div data-id="element-749">
                      <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-750">
                        {t('companies.form.labels.crewNumber')}{' '}
                        <span className="text-red-500" data-id="element-751">*</span>
                      </label>
                      <input type="number" value={crewNumber} onChange={e => setCrewNumber(e.target.value)} required min="1" placeholder="e.g., 5" className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-752" />
                    </div>
                    <div data-id="element-753">
                      <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-754">
                        {t('companies.form.labels.location')}
                      </label>
                      <select value={location} onChange={e => setLocation(e.target.value)} className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors appearance-none bg-white" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath fill='%23666' d='M4.427 6.427l3.396 3.396a.25.25 0 00.354 0l3.396-3.396A.25.25 0 0011.396 6H4.604a.25.25 0 00-.177.427z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 12px center'
                    }} data-id="element-755">
                        <option value="" data-id="element-756">
                          {t('companies.form.labels.selectLocation')}
                        </option>
                        {regions.map((region: string) => <option key={region} value={region} data-id="element-757">
                            {region}
                          </option>)}
                      </select>
                    </div>
                  </div>

                  <div className="mt-4" data-id="element-758">
                    <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-759">
                      {t('companies.form.labels.additional')}
                    </label>
                    <textarea value={additional} onChange={e => setAdditional(e.target.value)} rows={4} placeholder={t('companies.form.labels.additionalPlaceholder')} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors resize-none" data-id="element-760" />
                  </div>
                </div>

                {/* Timeline */}
                <div data-id="element-761">
                  <h3 className="text-lg font-semibold text-[var(--color-text-dark)] mb-4" data-id="element-762">
                    {t('companies.form.sections.timeline')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-763">
                    <div data-id="element-764">
                      <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-765">
                        {t('companies.form.labels.startDate')}{' '}
                        <span className="text-red-500" data-id="element-766">*</span>
                      </label>
                      <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} required className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors" data-id="element-767" />
                    </div>
                    <div data-id="element-768">
                      <label className="block text-sm font-medium text-[var(--color-text-dark)] mb-2" data-id="element-769">
                        {t('companies.form.labels.duration')}
                      </label>
                      <select value={duration} onChange={e => setDuration(e.target.value)} className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[var(--color-tan)] transition-colors appearance-none bg-white" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cpath fill='%23666' d='M4.427 6.427l3.396 3.396a.25.25 0 00.354 0l3.396-3.396A.25.25 0 0011.396 6H4.604a.25.25 0 00-.177.427z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 12px center'
                    }} data-id="element-770">
                        <option value="" data-id="element-771">
                          {t('companies.form.labels.selectDuration')}
                        </option>
                        <option value="1-month" data-id="element-772">{durations.m1}</option>
                        <option value="2-months" data-id="element-773">{durations.m2}</option>
                        <option value="3-months" data-id="element-774">{durations.m3}</option>
                        <option value="4-months" data-id="element-775">{durations.m4}</option>
                        <option value="6-months" data-id="element-776">{durations.m6}</option>
                        <option value="9-months" data-id="element-777">{durations.m9}</option>
                        <option value="12-months" data-id="element-778">{durations.m12}</option>
                        <option value="ongoing" data-id="element-779">{durations.ongoing}</option>
                        <option value="project-based" data-id="element-780">
                          {durations.project}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Consent */}
                <div className="pt-4" data-id="element-781">
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-782">
                    <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} className="mt-1 w-4 h-4 text-[var(--color-tan)] border-gray-300 rounded focus:ring-[var(--color-tan)]" data-id="element-783" />
                    <span className="text-sm text-[var(--color-text-dark)]" data-id="element-784">
                      {t('companies.form.labels.consent')}{' '}
                      <span className="text-red-500" data-id="element-785">*</span>
                    </span>
                  </label>
                </div>

                {submitStatus === 'success' && <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm" data-id="element-786">
                    <CheckCircle className="w-5 h-5 flex-shrink-0" data-id="element-787" />
                    <span className="font-medium" data-id="element-788">{t('formStatus.sent')}</span>
                  </div>}
                {submitStatus === 'error' && <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-id="element-789">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" data-id="element-790" />
                    <span data-id="element-791">{t('formStatus.error')}</span>
                  </div>}

                <div className="flex justify-center pt-2" data-id="element-792">
                  <button type="submit" disabled={!agreedToTerms || submitting} className={`px-8 py-4 text-white text-base font-medium rounded-lg transition-opacity flex items-center gap-2 ${agreedToTerms ? 'bg-[var(--color-tan)] hover:opacity-90' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-793">
                    {submitting ? <>
                        <LoaderIcon className="w-5 h-5 animate-spin" data-id="element-794" />
                        {t('formStatus.sending')}
                      </> : <>
                        <CheckCircle className="w-5 h-5" data-id="element-795" />
                        {t('companies.form.labels.submit')}
                      </>}
                  </button>
                </div>

                <p className="text-xs text-[var(--color-text-light)] text-center" data-id="element-796">
                  {t('companies.form.labels.disclaimer')}
                </p>
              </form>
            </div>
          </div>
        </section>
      </div>

      {/* Contact */}
      <div className="cv-lazy-section-sm" data-id="element-797">
        <section id="contact" className="w-full px-6 md:px-12 py-16 bg-[var(--color-dark)]" data-id="element-798">
          <div className="max-w-4xl mx-auto text-center" data-id="element-799">
            <div className="inline-block px-4 py-1.5 border border-gray-600 rounded-full mb-6" data-id="element-800">
              <span className="text-sm font-medium text-white" data-id="element-801">
                {t('companies.contact.pill')}
              </span>
            </div>
            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-802">
              {t('companies.contact.title')}
            </h2>
            <p className="text-lg text-gray-400 mb-8" data-id="element-803">
              {t('companies.contact.desc')}
            </p>

            <div className="flex flex-wrap justify-center gap-4" data-id="element-804">
              <a href="https://api.whatsapp.com/send/?phone=380731356233" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors" data-id="element-805">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" data-id="element-806">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" data-id="element-807" />
                </svg>
                <span className="text-sm font-medium" data-id="element-808">WhatsApp</span>
              </a>
              <a href="https://www.linkedin.com/company/111293896/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors" data-id="element-809">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" data-id="element-810">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" data-id="element-811" />
                </svg>
                <span className="text-sm font-medium" data-id="element-812">LinkedIn</span>
              </a>
              <a href="mailto:info@elvora.consulting" className="flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-colors" data-id="element-813">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-814">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" data-id="element-815" />
                </svg>
                <span className="text-sm font-medium" data-id="element-816">
                  info@elvora.consulting
                </span>
              </a>
            </div>
          </div>
        </section>
      </div>

      <Footer hideSocials data-id="element-817" />

      <CompanyFormModal isOpen={companyModalOpen} onClose={() => setCompanyModalOpen(false)} data-id="element-818" />
      <CVFormModal isOpen={cvModalOpen} onClose={() => setCvModalOpen(false)} mode="seafarer" data-id="element-819" />
    </div>;
}